import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import { MicrosoftExcelLogo, CaretLeft, CaretRight } from "@phosphor-icons/react";
import api, { money } from "../../lib/api";
import { DataTable } from "../../components/DataTable";
import { Button } from "../../components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../components/ui/sheet";

const when = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

function exportLog(rows) {
  const aoa = [["Invoice No.", "Customer", "Invoice Date", "Total", "Paid (reversed)", "Deleted By", "Deleted At", "Reason"],
    ...rows.map((r) => [r.doc_no, r.document?.customer_name, r.document?.date, r.document?.total, r.document?.amount_paid || 0, r.deleted_by, when(r.deleted_at), r.reason])];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), "Deleted Invoices");
  XLSX.writeFile(wb, `deleted-invoices-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

function SheetNav({ list, rec, onPick }) {
  const idx = list.findIndex((r) => r.id === rec?.id);
  const prev = idx > 0 ? list[idx - 1] : null;
  const next = idx >= 0 && idx < list.length - 1 ? list[idx + 1] : null;
  useEffect(() => {
    const onKey = (e) => {
      if (!e.altKey || (e.key !== "ArrowLeft" && e.key !== "ArrowRight")) { return; }
      e.preventDefault();
      const to = e.key === "ArrowLeft" ? prev : next;
      if (to) { onPick(to); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  if (idx < 0 || list.length < 2) { return null; }
  const btn = "h-8 w-8 inline-flex items-center justify-center border border-border rounded-sm transition-colors hover:bg-muted disabled:opacity-35 disabled:pointer-events-none";
  return (
    <div className="inline-flex items-center gap-1.5 mt-2" data-testid="deleted-invoice-nav">
      <button className={btn} disabled={!prev} onClick={() => onPick(prev)} title="Previous (Alt+←)" data-testid="deleted-invoice-nav-prev"><CaretLeft size={16} weight="bold" /></button>
      <span className="text-xs text-muted-foreground tabular min-w-[4.5rem] text-center" data-testid="deleted-invoice-nav-position">{idx + 1} <span>of</span> {list.length}</span>
      <button className={btn} disabled={!next} onClick={() => onPick(next)} title="Next (Alt+→)" data-testid="deleted-invoice-nav-next"><CaretRight size={16} weight="bold" /></button>
    </div>
  );
}

function DeletedInvoiceDetail({ rec, onClose, list, onPick }) {
  const d = rec?.document || {};
  return (
    <Sheet open={!!rec} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="deleted-invoice-detail">
        <SheetHeader>
          <SheetTitle>{rec?.doc_no} <span className="text-xs font-semibold text-red-600 align-middle ml-1">DELETED</span></SheetTitle>
          <SheetDescription>Deleted by {rec?.deleted_by} on {when(rec?.deleted_at)}</SheetDescription>
          <SheetNav list={list} rec={rec} onPick={onPick} />
        </SheetHeader>
        <div className="mt-4 space-y-4 text-sm">
          <div className="border border-amber-500/40 bg-amber-500/10 rounded-sm p-3" data-testid="deleted-invoice-reason"><p className="text-[11px] font-bold uppercase tracking-wider text-amber-700 mb-1">Reason</p>{rec?.reason}</div>
          <div className="grid grid-cols-2 gap-2">
            <p><span className="text-muted-foreground">Customer:</span> {d.customer_name}</p>
            <p><span className="text-muted-foreground">Invoice date:</span> {d.date}</p>
            <p><span className="text-muted-foreground">External doc:</span> {d.external_doc_no || "—"}</p>
            <p><span className="text-muted-foreground">Source order:</span> {d.source_order_no || "—"}</p>
          </div>
          <table className="w-full text-sm border border-border">
            <thead><tr className="bg-secondary/50 text-[10px] uppercase tracking-wider text-muted-foreground"><th className="text-left p-2">Description</th><th className="text-right p-2">Qty</th><th className="text-right p-2">Unit Price</th><th className="text-right p-2">Amount</th></tr></thead>
            <tbody>{(d.lines || []).filter((l) => l.description).map((l, i) => (
              <tr key={i} className="border-t border-border"><td className="p-2">{l.description}</td><td className="p-2 text-right tabular">{l.quantity}</td><td className="p-2 text-right tabular">{money(l.unit_price)}</td><td className="p-2 text-right tabular">{money(l.amount ?? l.quantity * l.unit_price)}</td></tr>
            ))}</tbody>
            <tfoot className="font-semibold">
              <tr className="border-t border-border"><td colSpan={3} className="p-2 text-right">Subtotal</td><td className="p-2 text-right tabular">{money(d.subtotal)}</td></tr>
              <tr><td colSpan={3} className="p-2 text-right">Tax</td><td className="p-2 text-right tabular">{money(d.tax)}</td></tr>
              <tr><td colSpan={3} className="p-2 text-right">Total</td><td className="p-2 text-right tabular" data-testid="deleted-invoice-total">{money(d.total)}</td></tr>
            </tfoot>
          </table>
          <div>
            <p className="text-xs font-semibold mb-1">Payments reversed ({(rec?.payments || []).length})</p>
            {(rec?.payments || []).length === 0 ? <p className="text-muted-foreground text-xs">None</p> : (
              <div className="border border-border rounded-sm divide-y divide-border" data-testid="deleted-invoice-payments">
                {rec.payments.map((p) => <div key={p.id} className="flex justify-between px-3 py-1.5 text-xs"><span>{(p.date || p.created_at || "").slice(0, 10)} · {p.method}</span><span className="tabular font-semibold">{money(p.amount)}</span></div>)}
              </div>
            )}
          </div>
          {(rec?.shipments || []).length > 0 && <p className="text-xs text-muted-foreground">Shipments removed: {rec.shipments.map((s) => s.shipment_no).join(", ")}</p>}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function DeletedInvoices() {
  const [open, setOpen] = useState(null);
  const { data = [], isLoading } = useQuery({ queryKey: ["deleted-invoices"], queryFn: async () => (await api.get("/deleted-invoices")).data });
  return (
    <>
      <DataTable
        testid="deleted-invoices-table" data={data} rowKey={(r) => r.id} rowTestid="deleted-invoice-row" loading={isLoading}
        rowProps={(r) => ({ onClick: () => setOpen(r), className: "cursor-pointer" })}
        empty="No posted invoices have been deleted." searchPlaceholder="Search deleted invoices…"
        toolbarExtra={(view) => (
          <Button size="sm" variant="outline" disabled={view.length === 0} onClick={() => exportLog(view)} className="h-8 rounded-sm text-xs gap-1.5" data-testid="deleted-invoices-export">
            <MicrosoftExcelLogo size={15} /> Export to Excel
          </Button>
        )}
        columns={[
          { key: "doc_no", header: "Invoice No.", filter: "text", cellClassName: "font-mono text-xs font-medium" },
          { key: "customer", header: "Customer", filter: "text", accessor: (r) => r.document?.customer_name || "", cellClassName: "text-sm font-medium" },
          { key: "date", header: "Invoice Date", accessor: (r) => r.document?.date || "", cellClassName: "text-sm font-mono text-muted-foreground" },
          { key: "total", header: "Total", align: "right", numeric: true, accessor: (r) => r.document?.total || 0, cell: (r) => <span className="tabular">{money(r.document?.total || 0)}</span> },
          { key: "paid", header: "Paid (reversed)", align: "right", numeric: true, accessor: (r) => r.document?.amount_paid || 0, cell: (r) => <span className="tabular">{money(r.document?.amount_paid || 0)}</span> },
          { key: "deleted_by", header: "Deleted By", filter: "select", cellClassName: "text-sm" },
          { key: "deleted_at", header: "Deleted At", cellClassName: "text-sm font-mono text-muted-foreground", cell: (r) => when(r.deleted_at) },
          { key: "reason", header: "Reason", filter: "text", cellClassName: "text-sm max-w-xs truncate", cell: (r) => <span title={r.reason} data-testid="deleted-invoice-reason-cell">{r.reason}</span> },
        ]}
      />
      <DeletedInvoiceDetail rec={open} onClose={() => setOpen(null)} onPick={setOpen}
        list={[...data].sort((a, b) => collator.compare(a.doc_no || "", b.doc_no || ""))} />
    </>
  );
}
